import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setActiveManifest, manifestForPath } from './InstallAppButton';

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

/**
 * Two responsibilities, kept together because both react to route changes:
 *
 * 1. **Manifest sync (Android Chrome)**: Reset the <link rel="manifest"> on
 *    every navigation so the install prompt always reflects the CURRENT page.
 *    Without this, a stale swap from a previously-visited /agent page leaks
 *    into /admin and the home-screen icon opens the wrong screen.
 *
 * 2. **Standalone launch redirect (iOS Safari)**: iOS ignores manifest
 *    `start_url` and launches at "/". We stamp `pwa_role` in localStorage so
 *    next standalone launch can route the user back to /admin, /partner or
 *    /agent.
 */
export const PWARoleRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // (1) Keep manifest + apple-touch-icon + theme colour in sync with the route.
  useEffect(() => {
    setActiveManifest(manifestForPath(location.pathname));
    // Stamp the role marker when we know which dashboard the user is on, so
    // a standalone iOS launch can later return them here.
    try {
      if (location.pathname.startsWith('/admin'))   localStorage.setItem('pwa_role', 'admin');
      else if (location.pathname.startsWith('/partner')) localStorage.setItem('pwa_role', 'partner');
      else if (location.pathname.startsWith('/agent'))   localStorage.setItem('pwa_role', 'agent');
    } catch (e) { /* localStorage unavailable */ }
  }, [location.pathname]);

  // (2) Standalone launch — if the app opened at "/" but we have a saved role,
  // redirect to that role's dashboard.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isStandalone()) return;
    try {
      const role = localStorage.getItem('pwa_role');
      if (!role) return;

      const targetMap = { admin: '/admin', partner: '/partner', agent: '/agent' };
      const target = targetMap[role];
      if (!target) return;

      if (location.pathname === '/' || location.pathname === '') {
        navigate(target, { replace: true });
      }
    } catch (e) {
      /* localStorage unavailable */
    }
  }, [location.pathname, navigate]);

  return null;
};

export default PWARoleRedirect;
